import React, { useState } from 'react';
import { Factory, MapPin, Globe, CheckCircle, ArrowRight, Loader2, Download, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';

function App() {
  const [service, setService] = useState('');
  const [baseLocation, setBaseLocation] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showResults, setShowResults] = useState(false);

  const neighborhoods = ["Chelsea", "Soho", "Kensington", "Camden", "Greenwich", "Westminster", "Hackney", "Islington"];

  const handleGenerate = () => {
    if (!service || !baseLocation) return;
    setIsGenerating(true);
    setProgress(0);
    
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          setShowResults(true);
          return 100;
        }
        return prev + 5;
      });
    }, 150);
  };

  return (
    <div className="localagent-app">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="logo">
          <Factory size={24} color="#00ff88" />
          <span>LocalAgent.io</span>
        </div>
        <nav>
          <div className="nav-item active"><Zap size={18} /> SEO Factory</div>
          <div className="nav-item"><Globe size={18} /> My Domains</div>
          <div className="nav-item"><CheckCircle size={18} /> Published</div>
        </nav>
        <div className="credits-badge">
          <span>LIFETIME ACCESS UNLOCKED</span>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="top-header">
          <h1>Generate Localized SEO Pages</h1>
          <p>Turn one service into 50 high-ranking neighborhood landing pages.</p>
        </header>

        {!showResults ? (
          <section className="generator-form">
            <div className="input-group">
              <label>What service do you provide?</label>
              <div className="input-wrapper">
                <Zap size={20} className="input-icon" />
                <input 
                  placeholder="e.g. Emergency Plumber, Dental Clinic, Legal Advice" 
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                />
              </div>
            </div>

            <div className="input-group">
              <label>Main City / Region</label>
              <div className="input-wrapper">
                <MapPin size={20} className="input-icon" />
                <input 
                  placeholder="e.g. London, Dubai, New York" 
                  value={baseLocation}
                  onChange={(e) => setBaseLocation(e.target.value)}
                />
              </div>
            </div>

            <button 
              className={`generate-btn ${isGenerating ? 'loading' : ''}`}
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="spin" size={20} />
                  Generating {progress}%
                </>
              ) : (
                <>Build Local Pages <ArrowRight size={20} /></>
              )}
            </button>

            {isGenerating && (
              <div className="progress-container">
                <motion.div 
                  className="progress-bar"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                />
                <div className="status-text">
                  Scraping {neighborhoods[Math.floor(progress/15)] || 'finalizing'} landmarks...
                </div>
              </div>
            )}
          </section>
        ) : (
          <motion.section 
            className="results-grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="results-header">
              <h2>🎉 Success! 50 Pages Generated for "{service}"</h2>
              <button className="cta-download"><Download size={18} /> Export Static Site (.zip)</button>
            </div>
            
            <div className="grid">
              {neighborhoods.map((n, i) => (
                <div key={i} className="page-card">
                  <div className="card-top">
                    <CheckCircle size={16} color="#00ff88" />
                    <span>Live Preview</span>
                  </div>
                  <h3>{service} in {n}</h3>
                  <p>Keyword density optimized. Local landmarks injected.</p>
                  <div className="card-footer">
                    <code>/{n.toLowerCase()}-{service.toLowerCase().replace(' ', '-')}</code>
                  </div>
                </div>
              ))}
              <div className="page-card more">
                <span>+42 more pages generated</span>
              </div>
            </div>
          </motion.section>
        )}
      </main>
    </div>
  );
}

export default App;
